import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import {
  filterSelectClass,
  formLabelClass,
  formSelectClass,
  selectDropdownOpenClass,
  selectDropdownOptionClass,
  selectDropdownPanelClass,
  selectDropdownPanelDropUpClass,
  selectDropdownPanelPortalClass,
} from './uiClasses.js';

const parseOptionChildren = (children) =>
  React.Children.toArray(children)
    .filter(React.isValidElement)
    .map((child) => ({
      value: child.props.value ?? '',
      label: child.props.children ?? child.props.value ?? '',
      disabled: Boolean(child.props.disabled),
      className: child.props.className || '',
    }));

/**
 * Custom select dropdown — same width, rounded-xl trigger, panel opens directly below.
 * Drop-in replacement for native <select> (supports option children + synthetic onChange events).
 */
const SelectField = ({
  value = '',
  onChange,
  name,
  id,
  options,
  children,
  label,
  error = false,
  required = false,
  disabled = false,
  placeholder,
  variant = 'form',
  className = '',
  labelClassName = formLabelClass,
  emptyLabel,
  dropUp = false,
  triggerClassName = '',
  portal = false,
}) => {
  const [open, setOpen] = useState(false);
  const [portalStyle, setPortalStyle] = useState(null);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const generatedId = useId();
  const fieldId = id || name || generatedId;

  const parsedOptions = useMemo(() => {
    if (Array.isArray(options) && options.length > 0) return options;
    return parseOptionChildren(children);
  }, [options, children]);

  const selectedOption = parsedOptions.find((opt) => String(opt.value) === String(value));
  const displayLabel =
    selectedOption?.label ?? placeholder ?? emptyLabel ?? (variant === 'filter' ? 'All' : 'Select...');

  const hasErrorClass =
    error || (typeof className === 'string' && className.includes('border-red-400'));
  const resolvedTriggerClass = triggerClassName
    ? triggerClassName
    : variant === 'filter'
      ? filterSelectClass
      : formSelectClass(hasErrorClass);

  const wrapperWidthClass =
    typeof className === 'string'
      ? className
          .split(/\s+/)
          .filter((token) => /^(w-|min-w-|max-w-)/.test(token))
          .join(' ')
      : '';

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inTrigger = containerRef.current?.contains(event.target);
      const inPanel = panelRef.current?.contains(event.target);
      if (!inTrigger && !inPanel) setOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open || !portal) {
      setPortalStyle(null);
      return undefined;
    }

    const updatePosition = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const gap = 4;
      const spaceBelow = window.innerHeight - rect.bottom - 8;
      const spaceAbove = rect.top - 8;
      const openUp = dropUp || (spaceBelow < 140 && spaceAbove > spaceBelow);
      const maxHeight = Math.max(120, Math.min(288, openUp ? spaceAbove - gap : spaceBelow - gap));
      setPortalStyle({
        left: rect.left,
        width: rect.width,
        maxHeight,
        ...(openUp
          ? { top: 'auto', bottom: window.innerHeight - rect.top + gap }
          : { top: rect.bottom + gap, bottom: 'auto' }),
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, portal, dropUp]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const emitChange = (newValue) => {
    onChange?.({ target: { name, value: newValue } });
  };

  const handleSelect = (optValue) => {
    if (disabled) return;
    emitChange(optValue);
    setOpen(false);
  };

  const renderList = () => {
    const list = (
      <ul
        ref={panelRef}
        role="listbox"
        className={
          portal
            ? selectDropdownPanelPortalClass
            : dropUp
              ? selectDropdownPanelDropUpClass
              : selectDropdownPanelClass
        }
        style={portal ? portalStyle || { visibility: 'hidden' } : undefined}
        aria-labelledby={fieldId}
      >
        {parsedOptions.map((opt) => {
          const isSelected = String(opt.value) === String(value);
          return (
            <li key={`${String(opt.value)}-${opt.label}`} role="option" aria-selected={isSelected}>
              <button
                type="button"
                disabled={opt.disabled}
                onClick={() => !opt.disabled && handleSelect(opt.value)}
                className={`${selectDropdownOptionClass(isSelected)} ${opt.className || ''} disabled:cursor-not-allowed disabled:opacity-50`}
              >
                {opt.label}
              </button>
            </li>
          );
        })}
      </ul>
    );
    return portal && typeof document !== 'undefined' ? createPortal(list, document.body) : list;
  };

  return (
    <div
      className={`relative min-w-0 ${wrapperWidthClass || 'w-full'} ${open ? 'z-[80]' : ''}`}
      ref={containerRef}
      style={open ? { zIndex: 80 } : undefined}
    >
      {label ? (
        <label htmlFor={fieldId} className={labelClassName}>
          {label} {required ? <span className="text-red-500">*</span> : null}
        </label>
      ) : null}

      <button
        type="button"
        ref={triggerRef}
        id={fieldId}
        name={name}
        disabled={disabled}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`${resolvedTriggerClass} flex w-full items-center justify-between gap-2 text-left ${
          disabled ? 'cursor-not-allowed opacity-60' : ''
        } ${open ? selectDropdownOpenClass : ''}`}
        title={typeof displayLabel === 'string' ? displayLabel : undefined}
      >
        <span
          className={`min-w-0 flex-1 whitespace-normal break-words line-clamp-2 ${
            !selectedOption && (placeholder || emptyLabel) ? 'text-gray-500 font-normal' : ''
          }`}
        >
          {displayLabel}
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 transition-transform ${
            open ? 'rotate-180 text-blue-500' : 'text-gray-500'
          }`}
          aria-hidden="true"
        />
      </button>

      {open ? renderList() : null}
    </div>
  );
};

export default SelectField;
